import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { getPreviewAudioEngine } from '../../engine/previewAudio';

const DB_MIN = -60;
const DB_MAX = 0;
const WIDTH_MIN = 36;
const WIDTH_MAX = 120;
const WIDTH_DEFAULT = 52;
const STORAGE_KEY = 'cinegrid.timeline.meterWidth';

/** dBFS labels drawn on the scale (top = 0). */
const DB_MARKS = [0, -6, -12, -18, -24, -36, -48, -60] as const;

function loadWidth(): number {
  try {
    const n = Number(localStorage.getItem(STORAGE_KEY));
    if (Number.isFinite(n)) return Math.min(WIDTH_MAX, Math.max(WIDTH_MIN, n));
  } catch {
    /* ignore */
  }
  return WIDTH_DEFAULT;
}

function linToDb(lin: number): number {
  if (lin <= 1e-8) return DB_MIN;
  return Math.max(DB_MIN, Math.min(DB_MAX, 20 * Math.log10(lin)));
}

/** Map dB → 0..1 from bottom of meter. */
function dbToY(db: number): number {
  return (db - DB_MIN) / (DB_MAX - DB_MIN);
}

function cssVar(name: string, fallback: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

/** Broadcast color bands: green / yellow / red by dB. */
function colorForDb(db: number, lit: boolean): string {
  let base: string;
  if (db >= -6) base = cssVar('--cg-danger', '#e45757');
  else if (db >= -18) base = cssVar('--cg-accent', '#e8a317');
  else base = cssVar('--cg-playhead', '#3ecf8e');
  if (!lit) {
    // Dim unlit segments of the same band
    return base;
  }
  return base;
}

/** Vertical stereo peak meter — resizable, dB scale, color bands. */
export function TimelineAudioMeter() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [width, setWidth] = useState(loadWidth);
  const widthRef = useRef(width);
  widthRef.current = width;

  const onResizePointerDown = useCallback((e: ReactPointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = widthRef.current;
    const target = e.currentTarget as HTMLElement;
    target.setPointerCapture(e.pointerId);

    const onMove = (ev: PointerEvent) => {
      // Dragging the left edge: move left → wider
      const next = Math.min(WIDTH_MAX, Math.max(WIDTH_MIN, startW + (startX - ev.clientX)));
      setWidth(next);
    };
    const onUp = (ev: PointerEvent) => {
      target.releasePointerCapture(ev.pointerId);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      try {
        localStorage.setItem(STORAGE_KEY, String(Math.round(widthRef.current)));
      } catch {
        /* ignore */
      }
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, []);

  useEffect(() => {
    let raf = 0;
    let alive = true;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const paint = () => {
      if (!alive) return;
      const levels = getPreviewAudioEngine().getMeterLevels();
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const cssW = canvas.clientWidth || widthRef.current;
      const cssH = canvas.clientHeight || 120;
      const nextW = Math.max(1, Math.floor(cssW * dpr));
      const nextH = Math.max(1, Math.floor(cssH * dpr));
      if (canvas.width !== nextW || canvas.height !== nextH) {
        canvas.width = nextW;
        canvas.height = nextH;
      }
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        raf = requestAnimationFrame(paint);
        return;
      }

      const w = canvas.width;
      const h = canvas.height;
      const bg = cssVar('--cg-bg-base', '#15171a');
      const hair = cssVar('--cg-border', '#2a2e33');
      const textLow = cssVar('--cg-text-low', '#8b9199');
      const textHigh = cssVar('--cg-text-high', '#edeeef');

      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, w, h);

      // Scale column on the left (~38% of width for labels)
      const scaleW = Math.max(18 * dpr, Math.floor(w * 0.38));
      const barsArea = w - scaleW;
      const gap = Math.max(1, Math.round(dpr));
      const barW = Math.max(2, Math.floor((barsArea - gap * 3) / 2));
      const leftX = scaleW + gap;
      const rightX = scaleW + gap * 2 + barW;

      // Color zone backgrounds (always visible bands)
      const drawBand = (dbLo: number, dbHi: number, color: string, alpha: number) => {
        const yTop = h * (1 - dbToY(dbHi));
        const yBot = h * (1 - dbToY(dbLo));
        ctx.globalAlpha = alpha;
        ctx.fillStyle = color;
        ctx.fillRect(leftX, yTop, barW * 2 + gap, Math.max(1, yBot - yTop));
        ctx.globalAlpha = 1;
      };
      const green = cssVar('--cg-playhead', '#3ecf8e');
      const yellow = cssVar('--cg-accent', '#e8a317');
      const red = cssVar('--cg-danger', '#e45757');
      drawBand(DB_MIN, -18, green, 0.08);
      drawBand(-18, -6, yellow, 0.1);
      drawBand(-6, DB_MAX, red, 0.12);

      const drawChannel = (x: number, lin: number, peakLin: number) => {
        const db = linToDb(lin);
        const peakDb = linToDb(peakLin);
        const segs = Math.max(24, Math.floor(h / (2.5 * dpr)));
        const segH = h / segs;
        const lit = Math.round(dbToY(db) * segs);

        for (let i = 0; i < segs; i++) {
          const fromBottom = i;
          const y = h - (fromBottom + 1) * segH;
          const t = (fromBottom + 0.5) / segs;
          const segDb = DB_MIN + t * (DB_MAX - DB_MIN);
          const on = fromBottom < lit;
          ctx.globalAlpha = on ? 0.95 : 0.18;
          ctx.fillStyle = colorForDb(segDb, on);
          ctx.fillRect(x, y + gap * 0.3, barW, Math.max(1, segH - gap * 0.6));
        }

        if (peakLin > 0.001) {
          const py = h * (1 - dbToY(peakDb));
          ctx.globalAlpha = 1;
          ctx.fillStyle = peakDb >= -1 ? red : textHigh;
          ctx.fillRect(x, Math.max(0, py - dpr), barW, Math.max(1.5 * dpr, dpr));
        }
        ctx.globalAlpha = 1;
      };

      drawChannel(leftX, levels.left, levels.peakLeft);
      drawChannel(rightX, levels.right, levels.peakRight);

      // dB scale markings + numbers
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      const fontPx = Math.max(8, Math.min(11, Math.round(10 * (cssW / 52)))) * dpr;
      ctx.font = `500 ${fontPx}px ui-sans-serif, system-ui, sans-serif`;

      for (const mark of DB_MARKS) {
        const y = h * (1 - dbToY(mark));
        ctx.globalAlpha = 0.55;
        ctx.strokeStyle = hair;
        ctx.lineWidth = Math.max(1, dpr * 0.5);
        ctx.beginPath();
        ctx.moveTo(scaleW - 3 * dpr, y);
        ctx.lineTo(scaleW, y);
        // faint guide across bars
        ctx.moveTo(leftX, y);
        ctx.lineTo(leftX + barW * 2 + gap, y);
        ctx.stroke();

        ctx.globalAlpha = mark === 0 || mark === -18 ? 0.95 : 0.7;
        ctx.fillStyle = mark >= -6 ? red : mark >= -18 ? yellow : textLow;
        const label = mark === 0 ? '0' : String(mark);
        ctx.fillText(label, scaleW - 4 * dpr, y);
      }
      ctx.globalAlpha = 1;

      // Tiny "dB" caption
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.font = `500 ${Math.max(7, fontPx * 0.75)}px ui-sans-serif, system-ui, sans-serif`;
      ctx.fillStyle = textLow;
      ctx.globalAlpha = 0.65;
      ctx.fillText('dB', scaleW / 2, 2 * dpr);
      ctx.globalAlpha = 1;

      raf = requestAnimationFrame(paint);
    };

    raf = requestAnimationFrame(paint);
    return () => {
      alive = false;
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div
      className="relative flex shrink-0 flex-col border-l border-hairline bg-panel"
      style={{ width }}
      title="Audio levels (dBFS) — drag left edge to resize"
    >
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize audio meter"
        className="absolute inset-y-0 left-0 z-10 w-1.5 cursor-ew-resize hover:bg-accent/40 active:bg-accent/60"
        onPointerDown={onResizePointerDown}
      />
      <div className="flex h-7 shrink-0 items-center justify-between gap-1 border-b border-hairline px-1.5 pl-2.5">
        <span className="text-[9px] font-semibold uppercase tracking-wide text-text-low">VU</span>
        <span className="flex items-center gap-0.5" aria-hidden>
          <span className="h-1.5 w-1.5 rounded-sm bg-[var(--cg-playhead)]" title="≤ −18 dB" />
          <span className="h-1.5 w-1.5 rounded-sm bg-accent" title="−18…−6 dB" />
          <span className="h-1.5 w-1.5 rounded-sm bg-danger" title="≥ −6 dB" />
        </span>
      </div>
      <div className="relative min-h-0 flex-1 py-1 pl-1 pr-0.5">
        <canvas ref={canvasRef} className="block h-full w-full" />
      </div>
      <div
        className="flex shrink-0 pb-1 text-[8px] text-text-low"
        style={{ paddingLeft: '38%' }}
      >
        <span className="flex-1 text-center">L</span>
        <span className="flex-1 text-center">R</span>
      </div>
    </div>
  );
}
